import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Activity, RefreshCw, Square } from 'lucide-react';
import { Badge, Btn, C, Card, statusLabel } from '../ui/kit';
import api from '../services/api';
import PreismakerMetrics from './PreismakerMetrics';
import PreismakerCoordinator, { coordinatorAllowsStart, isCoordinatorValid } from './PreismakerCoordinator';
import './PreismakerLive.css';
import { datumUndZeit } from '../lib/zeit';

const REFRESH_MS = 3000;
const STALE_MS = 6000;
const UNKNOWN = 'Unbekannt';
const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const euros = (value) => Number.isSafeInteger(value)
  ? (value / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : UNKNOWN;
const number = (value, suffix = '') => isNumber(value) ? `${value.toLocaleString('de-DE')}${suffix}` : UNKNOWN;
const percent = (value) => isNumber(value) ? number(value * 100, ' %') : UNKNOWN;
const time = (value) => (isNumber(value) ? datumUndZeit(value, { jahr: 'voll', sekunden: true, leer: UNKNOWN }) : UNKNOWN);
const duration = (value) => isNumber(value) ? number(value / 1000, ' s') : UNKNOWN;
const text = (value) => typeof value === 'string' && value.trim() ? value : UNKNOWN;
const stateLabel = (value) => value === 'live' ? 'Live' : value === 'stopped' ? 'Gestoppt' : UNKNOWN;
const OUTCOMES = { pending: 'Ergebnis ausstehend', booked: 'Gebucht', completed: 'Abgeschlossen', not_booked: 'Nicht gebucht', excluded: 'Vom Lernen ausgeschlossen', no_driver: 'Kein Fahrer gefunden', cancelled: 'Fahrt storniert' };
const ARMS = { baseline: 'Unverändert', lower: 'Günstiger', higher: 'Höher' };
const REASONS = {
  baseline: 'Basisangebot', stopped: 'Preismaker gestoppt', stable_intent: 'Bestehende Angebotsabsicht beibehalten',
  bounded_exploration: 'Begrenzte Erprobung', posterior_improvement: 'Aus bisherigen Ergebnissen gewählt',
  insufficient_evidence: 'Noch zu wenige belastbare Ergebnisse', outside_scope: 'Außerhalb der gewählten Städte',
  learning_not_allowed: 'Lernen nicht freigegeben', engine_guard: 'Preisgrenze der Engine', deadline: 'Zeitlimit erreicht',
  budget_bound: 'Budgetgrenze erreicht', stopped_or_revision_changed: 'Gestoppt oder Betriebsstand geändert', data_expired: 'Datengrundlage veraltet',
  engine_revision_changed: 'Engine-Einstellungen zwischenzeitlich geändert', concurrent_intent: 'Gleichzeitige Angebotsanfrage',
  learning_unavailable: 'Lernen derzeit nicht verfügbar', settlement_not_reported: 'Abrechnungsgrund nicht gemeldet',
  learning_data_not_started: 'Lerndatenschnitt noch nicht gestartet', telemetry_not_collect: 'Lerndatenerfassung ist ausgeschaltet',
  analytics_consent_missing: 'Keine nutzergebundene Analytics-Einwilligung vorhanden', analytics_consent_declined: 'Analytics-Einwilligung nicht erteilt',
  learning_provenance_unavailable: 'Lernfreigabe für dieses Angebot nicht belegt', original_telemetry_unavailable: 'Ursprüngliche Lernprovenienz fehlt oder wurde widerrufen',
  decision_learning_ineligible: 'Angebote haben keine gemeinsame gültige Lernzuordnung', awaiting_outcome: 'Wartet auf ein verwertbares Ergebnis',
  outcome_excluded: 'Ergebnis eignet sich nicht als Lernsignal', learning_stopped: 'Modellübernahme gestoppt', learning_revoked: 'Lernbeleg widerrufen',
  learned: 'Ein Ergebnis in das Modell übernommen', legacy_generation: 'Frühere Modellgeneration', offer_group_incomplete: 'Angebotsvorgang noch nicht vollständig ausgegeben',
  snapshot_not_used: 'Marktsnapshot nicht verwendet', snapshot_not_used_reservation: 'Bei Reservierungen nicht verwendet',
  snapshot_config_invalid: 'Snapshot-Konfiguration ungültig', snapshot_config_mismatch: 'Snapshot passt nicht zur Konfiguration',
  snapshot_provenance_mismatch: 'Snapshot hat unpassende Datenherkunft', snapshot_missing: 'Marktsnapshot fehlt', snapshot_stale: 'Marktsnapshot veraltet',
  snapshot_age_unavailable: 'Snapshot-Alter nicht übermittelt', snapshot_measured: 'Alter am Angebot gemessen',
  legacy_learning_reason_unavailable: 'Lerngrund im Altbestand nicht erfasst', legacy_snapshot_reason_unavailable: 'Snapshot-Grund im Altbestand nicht erfasst',
  settlement_correction_pending: 'Korrigierte Abrechnungsaufteilung ausstehend', settlement_ledger_pending: 'Abschlussbuchung ausstehend',
  settlement_components_missing: 'Abrechnungsbestandteile fehlen', settlement_posted: 'Abrechnung gebucht',
  driver_searching: 'Fahrersuche läuft', driver_assigned: 'Fahrer hat angenommen', driver_arrived: 'Fahrer ist angekommen', ride_in_progress: 'Fahrt läuft',
  no_driver: 'Kein Fahrer gefunden – keine Preisablehnung', cancelled: 'Fahrt storniert – keine Preisablehnung',
  not_booked: 'Nicht bestellt', booking_not_recorded: 'Noch keine Bestellung erfasst', ride_status_unavailable: 'Fahrtstatus nicht verfügbar',
};
const reasonLabel = (value) => REASONS[value] || text(value);

function checkedState(data) {
  if (!data || !['live', 'stopped'].includes(data.state) || !Number.isSafeInteger(data.revision)
    || data.revision < 0 || !isNumber(data.as_of) || !data.config || !data.budget
    || !Array.isArray(data.decisions) || typeof data.can_start !== 'boolean'
    || typeof data.can_stop !== 'boolean') throw new Error('invalid_preismaker_state');
  return data;
}

// Parse decimal euros without floating-point rounding of money or blank -> zero coercion.
function budgetCents(value) {
  const match = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(value.trim());
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

function Datum({ label, children }) {
  return <div><dt>{label}</dt><dd>{children}</dd></div>;
}
const CLASS_LABELS = { standard:'Basis',comfort:'Komfort',premium:'Premium',xl:'XL',max:'Max' };
const coordinates = value => Array.isArray(value) && value.length>=2 && value.slice(0,2).every(isNumber)
  ? value.slice(0,2).map(n=>n.toFixed(5)).join(', ') : 'Koordinaten nicht verfügbar';
function GroupRoute({ context }) {
  if (context?.status!=='available') return <small>{context?.status==='not_authorized'
    ? 'Routendetails benötigen die Berechtigung für genaue Standorte.' : 'Routenkontext nicht mehr verfügbar (Aufbewahrung: 24 Stunden).'}</small>;
  return <><ol className="preismaker-route">
    <li>Abholung: {context.pickup_address || coordinates(context.pickup)}</li>
    {(context.stops || []).map((stop,index)=><li key={index}>Zwischenstopp {index+1}: {coordinates(stop)}</li>)}
    <li>Ziel: {context.dropoff_address || coordinates(context.dropoff)}</li>
  </ol><small>{context.scheduled_for ? `Geplant: ${datumUndZeit(context.scheduled_for, { jahr: 'voll' })}` : 'Sofortfahrt'} · {context.payment_method==='cash'?'Bar':context.payment_method==='card'?'Karte':'Zahlart nicht angegeben'}</small></>;
}

export default function PreismakerLive() {
  const [snapshot, setSnapshot] = useState(null);
  const [receivedAt, setReceivedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [readError, setReadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(null);
  const [cities, setCities] = useState([]);
  const [budget, setBudget] = useState('');
  const [reason, setReason] = useState('');
  const mounted = useRef(false);
  const epoch = useRef(0);
  const request = useRef(null);
  const mutation = useRef(null);
  const stateRef = useRef(null);
  const inputsInitialized = useRef(false);

  const accept = useCallback((data) => {
    const next = checkedState(data);
    // Also fence replica/delayed snapshots from before an already confirmed mutation.
    if (stateRef.current && next.revision < stateRef.current.revision) throw new Error('old_preismaker_state');
    stateRef.current = next;
    setSnapshot(next);
    setReceivedAt(Date.now());
    setNow(Date.now());
    setReadError('');
    if (!inputsInitialized.current) {
      setCities(Array.isArray(next.config.cities) ? next.config.cities : []);
      setBudget(Number.isSafeInteger(next.config.budget_cents) ? (next.config.budget_cents / 100).toFixed(2).replace('.', ',') : '');
      inputsInitialized.current = true;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!mounted.current || request.current || mutation.current) return;
    const current = { epoch: epoch.current, controller: new AbortController() };
    request.current = current;
    setRefreshing(true);
    try {
      const response = await api.get('/admin/preismaker/state', { signal: current.controller.signal, timeout: 10000 });
      if (mounted.current && epoch.current === current.epoch) accept(response.data);
    } catch {
      if (mounted.current && epoch.current === current.epoch) setReadError('Aktualisierung fehlgeschlagen. Der aktuelle Betriebszustand ist nicht bestätigt.');
    } finally {
      if (request.current === current) {
        request.current = null;
        if (mounted.current) setRefreshing(false);
      }
    }
  }, [accept]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const poll = setInterval(refresh, REFRESH_MS);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      mounted.current = false;
      epoch.current += 1;
      request.current?.controller.abort();
      request.current = null;
      clearInterval(poll);
      clearInterval(clock);
    };
  }, [refresh]);

  const stale = snapshot && (receivedAt === null || now - receivedAt > STALE_MS || now - snapshot.as_of > STALE_MS);
  const unconfirmed = Boolean(readError || stale);
  const cityOptions = Array.isArray(snapshot?.city_options) ? snapshot.city_options : [];
  const selectedCities = [...new Set(cities)];
  const validCities = selectedCities.length > 0 && selectedCities.every(id => cityOptions.some(city => city.id === id));
  const cents = budgetCents(budget);
  const coordinatorValid = isCoordinatorValid(snapshot?.coordinator);
  const startAllowed = snapshot?.state === 'stopped' && snapshot?.can_start === true && coordinatorAllowsStart(snapshot.coordinator, snapshot.state) && cityOptions.length > 0 && !unconfirmed && !busy;

  const changeState = async (action) => {
    const currentState = stateRef.current;
    if (!currentState || mutation.current === 'stop') return;
    if (action === 'start' && (!startAllowed || mutation.current || !validCities || cents === null || !reason.trim())) return;
    if (action === 'stop' && currentState.can_stop !== true) return;
    // STOP may supersede START. The server's revision fence decides ordering; an old
    // GET or START response can never restore the UI's earlier live state.
    const operationEpoch = ++epoch.current;
    request.current?.controller.abort();
    request.current = null;
    mutation.current = action;
    setRefreshing(false);
    setBusy(action);
    setActionError('');
    const body = action === 'stop' ? { reason: reason.trim() || 'Manueller Stopp in der Admin-Liveansicht' }
      : { expected_revision: currentState.revision, cities: selectedCities, budget_cents: cents, reason: reason.trim() };
    let reload = false;
    try {
      const response = await api.post(`/admin/preismaker/${action}`, body, { timeout: 10000 });
      if (mounted.current && epoch.current === operationEpoch) accept(response.data);
    } catch (error) {
      if (mounted.current && epoch.current === operationEpoch) {
        const conflict = error.response?.status === 409;
        setActionError(conflict
          ? 'Der Zustand wurde zwischenzeitlich geändert. Neu geladen; zum Start erneut bewusst klicken.'
          : `${action === 'stop' ? 'Stopp' : 'Start'} nicht bestätigt. Der Serverzustand wird neu geladen; es erfolgt keine automatische Wiederholung.`);
        setReadError('Betriebszustand wird erneut geprüft.');
        reload = true;
      }
    } finally {
      if (epoch.current === operationEpoch) {
        mutation.current = null;
        if (mounted.current) {
          setBusy(null);
          if (reload) refresh();
        }
      }
    }
  };

  const config = snapshot?.config;
  const hourMetrics = snapshot?.metrics?.windows?.find(window=>window.minutes===60);
  const grouped = hourMetrics?.offer_requests;
  const currentLabel = busy === 'stop' ? 'Stopp wird bestätigt …' : busy === 'start' ? 'Start wird bestätigt …'
    : unconfirmed ? 'Zustand nicht bestätigt' : snapshot ? stateLabel(snapshot.state) : 'Zustand wird geladen …';

  return (
    <section className="preismaker-live" aria-label="Preismaker Live">
      <Card className="preismaker-command-card">
        <div className="preismaker-heading">
          <div><h2><Activity size={18} aria-hidden="true" /> Preismaker · Live</h2>
            <p>Gemeinsame Steuerung der koordinierten Optimierung innerhalb der bestehenden Preisgrenzen.</p></div>
          <Btn variant="danger" className="preismaker-stop" onClick={() => changeState('stop')}
            disabled={snapshot?.can_stop !== true || busy === 'stop'}>
            <Square size={18} aria-hidden="true" /> {busy === 'stop' ? 'STOPP wird bestätigt …' : 'STOPP'}
          </Btn>
        </div>
        <div className="preismaker-status" aria-live="polite">
          <span>Schaltzustand:</span>
          <Badge color={!unconfirmed && !busy && snapshot?.state === 'live' ? C.brand : C.muted}>
            <span data-testid="preismaker-state">{currentLabel}</span>
          </Badge>
          <span>Serverstand: {time(snapshot?.as_of)} (Berlin)</span>
          <Btn variant="secondary" onClick={refresh} disabled={refreshing || Boolean(busy)} aria-label="Preismaker aktualisieren">
            <RefreshCw size={14} aria-hidden="true" /> {refreshing ? 'Lädt …' : 'Aktualisieren'}
          </Btn>
        </div>
        {(unconfirmed || busy) && snapshot && <p>Zuletzt bestätigt: {stateLabel(snapshot.state)}. Aktueller Zustand wird geprüft.</p>}
        {readError && <p role="alert" className="preismaker-alert">{readError}</p>}
        {stale && <p role="alert" className="preismaker-alert">Daten veraltet. Der letzte Serverstand ist keine aktuelle Betriebsbestätigung.</p>}
        {actionError && <p role="alert" className="preismaker-alert">{actionError}</p>}
        <p>STOPP beendet die Preismaker-Auswahl, ihre Modellpublikation, zusätzliches V2-Behavior und V2-Proposal-Übernahmen. Basis-Engine, Wochenprofil und bestehende Window-Shopper-, Routen-, Bar- und Ruherabatte bleiben bestehen. Bereits gebundene Angebote bleiben gültig.</p>
        {snapshot?.can_stop === false && <p>Keine Berechtigung zum Stoppen.</p>}
      </Card>

      <PreismakerCoordinator coordinator={snapshot?.coordinator} stale={unconfirmed || Boolean(busy)} />
      <Card>
        <h3>Angebotsvorgänge · letzte 60 Minuten</h3>
        <dl className="preismaker-facts">
          <Datum label="Neue Vorgänge">{number(grouped?.created)}</Datum>
          <Datum label="Mindestens eine Klasse ausgegeben">{number(grouped?.issued)}</Datum>
          <Datum label="Alle Klassen ausgegeben">{number(grouped?.complete)}</Datum>
          <Datum label="Darstellung bestätigt">{number(grouped?.shown)}</Datum>
          <Datum label="Ausdrücklich verlassen">{number(grouped?.abandoned)}</Datum>
          <Datum label="Bestellt">{number(grouped?.booked)}</Datum>
          <Datum label="Alte Klassenabsichten ohne Gruppierung">{number(hourMetrics?.legacy_intents)}</Datum>
          <Datum label="Einzelne Preisberechnungen">{number(hourMetrics?.evaluated_count)}</Datum>
        </dl>
        <p>Ein Vorgang enthält bis zu fünf Klassen. Ungewählte Klassen zählen nicht als zusätzliche Kundenabbrüche. Alte Anfragen bleiben getrennt; sie werden nicht nachträglich durch fünf geteilt. Die technische Übersicht darunter zählt weiterhin jede Quote und Berechnung.</p>
      </Card>
      <PreismakerMetrics metrics={snapshot?.metrics} stale={unconfirmed} now={now} />

      {snapshot && <>
        <Card>
          <h3>Betrieb und Grenzen</h3>
          <dl className="preismaker-facts">
            <Datum label="Geltungsbereich">{Array.isArray(config.cities) ? config.cities.length ? config.cities.join(', ') : 'Keine Städte' : UNKNOWN}</Datum>
            <Datum label="Modell">{text(snapshot.model_version)}</Datum>
            <Datum label="Modell aktualisiert">{time(snapshot.model_updated_at)}</Datum>
            <Datum label="Letzte Entscheidung">{time(snapshot.health?.last_decision_at)}</Datum>
            <Datum label="Letzter Fehler">{snapshot.health?.last_error ? reasonLabel(snapshot.health.last_error) : 'Kein Fehler gemeldet'}</Datum>
            <Datum label="Betrieb geändert">{time(snapshot.updated_at)}</Datum>
            <Datum label="Testumfang gesamt">{euros(config.budget_cents)}</Datum>
            <Datum label="Testumfang verbraucht">{euros(snapshot.budget.committed_cents)}</Datum>
            <Datum label="Testumfang ausstehend">{euros(snapshot.budget.pending_cents)}</Datum>
            <Datum label="Testumfang restlich">{euros(snapshot.budget.available_cents)}</Datum>
            <Datum label="Faktorschritt höchstens">{number(config.max_delta)}</Datum>
            <Datum label="Exploration">{number(config.exploration_pct, ' %')}</Datum>
            <Datum label="Mindeststichprobe">{number(config.min_samples)}</Datum>
            <Datum label="Ergebnis-Reifezeit">{duration(config.maturity_ms)}</Datum>
            <Datum label="Maximales Datenalter">{duration(config.max_data_age_ms)}</Datum>
            <Datum label="Provisionsstufen">2–8 % in 0,5-Prozentpunkt-Schritten · Satz im Angebot gebunden</Datum>
          </dl>
          <p>Der Testumfang zählt kumulativ angebotene Fahrpreise über alle ausgewählten Städte. Eine Änderung von 20 auf 19 Euro belegt 20 Euro, nicht 1 Euro; jede veränderte Klassenvariante belegt ihren eigenen Betrag. Basisangebote belegen 0 Euro. Ausstehende Beträge werden beim Abschluss verbraucht. STOPP, START und Tageswechsel füllen nichts auf. Dies sind weder Rabattkosten noch ein nachgewiesener Verlust oder Gewinn.</p>
          <p>Ausgabe und bestätigte Darstellung sind getrennte Messungen. Auch eine bestätigte Darstellung beweist nicht, dass ein Mensch den Preis gelesen hat. Eine wirtschaftliche Verbesserung ist damit nicht nachgewiesen.</p>
        </Card>

        <Card>
          <h3>Koordinierte Optimierung bewusst starten</h3>
          <form onSubmit={(event) => { event.preventDefault(); changeState('start'); }}>
            <div className="preismaker-form">
              <label>Abholstädte<select multiple value={cities} onChange={(event) => setCities(Array.from(event.target.selectedOptions, option => option.value))} disabled={!startAllowed}>
                {cityOptions.map(city => <option key={city.id} value={city.id}>{city.label}</option>)}
              </select></label>
              <label>Budget in Euro<input inputMode="decimal" value={budget} onChange={(event) => setBudget(event.target.value)} disabled={!startAllowed} /></label>
              <label>Begründung<input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} disabled={Boolean(busy)} /></label>
            </div>
            <Btn type="submit" disabled={!startAllowed || !validCities || cents === null || !reason.trim()}>Koordinierte Optimierung starten</Btn>
          </form>
          <p>{snapshot.can_start !== true ? 'Keine Startberechtigung.' : snapshot.state === 'live' ? 'Vor jedem erneuten START zuerst STOPP bestätigen. Städte und Budget lassen sich nur im gestoppten Zustand ändern.' : !coordinatorValid ? 'Koordinator-Bereitschaft unbekannt. START bleibt gesperrt.' : !snapshot.coordinator.start_ready ? 'Globale Startvoraussetzungen fehlen. Die Gründe stehen in der gemeinsamen Bereitschaft.' : 'Start nur mit bestätigten globalen Voraussetzungen, Städten, positivem Budget und Begründung. Kein automatischer Neustart.'}</p>
          <p>Die Stadt folgt den serverseitigen Abholkoordinaten. Die Auswahl erweitert nicht das Servicegebiet. Entfernte Städte erhalten keine neuen Preismaker-Varianten; bereits gebundene Angebote und der verbrauchte Testumfang bleiben erhalten.</p>
          {!cityOptions.length && <p>Die gültigen Abholstädte sind noch nicht bestätigt.</p>}
        </Card>

        <Card>
          <h3>Angebotsvorgänge und Route</h3>
          <p>Je Vorgang ein Ergebnis; darunter bleiben die verbindlichen Klassenangebote einzeln prüfbar. Routendetails werden höchstens 24 Stunden bereitgestellt.</p>
          {(snapshot.offer_requests || []).map(group=><article key={group.id} data-testid={`preismaker-group-${group.id}`} className="preismaker-group">
            <strong>{time(group.created_at)} · {OUTCOMES[group.outcome] || UNKNOWN}</strong>
            <small>Vorgang: {group.id}</small>
            <GroupRoute context={group.route_context} />
            <small>{group.complete?'Alle Klassen ausgegeben':'Ausgabe unvollständig'} · {reasonLabel(group.learning_reason)}</small>
            {group.abandoned_at && <small>Ausdrücklich verlassen: {time(group.abandoned_at)}</small>}
            <ul>{group.members.map(member=><li key={member.quote_id}>{CLASS_LABELS[member.ride_type] || member.ride_type}: {member.quote_id} · {member.shown_at?'Darstellung bestätigt':member.issued_at?'Ausgabe bestätigt, Darstellung nicht bestätigt':'Ausgabe nicht bestätigt'}</li>)}</ul>
          </article>)}
          {!snapshot.offer_requests?.length && <p>Noch keine gemeinsam gebundenen Angebotsvorgänge im jüngsten Verlauf.</p>}
        </Card>

        <Card>
          <h3>Entscheidungsverlauf</h3>
          <p>Angebot, späterer Fahrpreis und offene Zahlungsbeträge sind getrennte Serverwerte. Die Provision enthält nur den prozentualen Anteil; die feste Servicegebühr steht separat. „Unbekannt“ bedeutet, dass kein Betrag vorliegt.</p>
          <div className="preismaker-table-scroll" tabIndex={0} role="region" aria-label="Entscheidungsverlauf, horizontal scrollbar">
            <table>
              <caption>Berechnete Angebote, bestätigte Ausgabe und spätere Ergebnisse</caption>
              <thead><tr><th scope="col">Zeit / Angebot</th><th scope="col">Entscheidung</th><th scope="col">Gebundenes Angebot</th><th scope="col">Spätere Abrechnung</th><th scope="col">Ergebnis / Lernen</th></tr></thead>
              <tbody>{snapshot.decisions.map((decision) => <tr key={decision.quote_id} data-testid={`preismaker-decision-${decision.quote_id}`}>
                <th scope="row">{time(decision.created_at)}<small>{text(decision.quote_id)}</small><small>{text(decision.city)}</small><small>{decision.offer_request_id?`Vorgang: ${decision.offer_request_id}`:'Altvertrag ohne Angebotsgruppe'}</small></th>
                <td><strong>{ARMS[decision.arm] || UNKNOWN}</strong><small>{reasonLabel(decision.reason)}</small>
                  <small>Modell: {text(decision.model_version)}</small><small>Marktsnapshot-Alter: {duration(decision.data_age_ms)} · {reasonLabel(decision.data_reason)}</small><small>Latenz: {number(decision.latency_ms, ' ms')}</small></td>
                <td><dl><Datum label="Basisangebot">{euros(decision.baseline_cents)}</Datum><Datum label="Kundenangebot">{euros(decision.offer_cents)}</Datum>
                  <Datum label="Fahrpreis im Angebot">{euros(decision.fare_cents)}</Datum><Datum label="Provisionssatz">{percent(decision.commission_rate)}</Datum><Datum label="Provision im Angebot">{euros(decision.commission_cents)}</Datum><Datum label="Feste Servicegebühr im Angebot">{euros(decision.service_cents)}</Datum><Datum label="Partneranteil im Angebot">{euros(decision.partner_cents)}</Datum></dl></td>
                <td><dl><Datum label="Späterer Fahrpreis">{euros(decision.settlement?.fare_cents)}</Datum>
                  <Datum label="Kunde noch fällig">{euros(decision.settlement?.customer_due_cents)}</Datum><Datum label="Davon bar fällig">{euros(decision.settlement?.cash_due_cents)}</Datum>
                  <Datum label="Tatsächliche Provision">{euros(decision.settlement?.commission_cents)}</Datum><Datum label="Tatsächliche Servicegebühr">{euros(decision.settlement?.service_cents)}</Datum><Datum label="Tatsächlicher Partneranteil">{euros(decision.settlement?.partner_cents)}</Datum></dl>
                  {decision.settlement && <small>{statusLabel[decision.settlement.status] || text(decision.settlement.status)} · {reasonLabel(decision.settlement.reason)}</small>}
                  <small>{reasonLabel(decision.settlement_reason)}</small>
                  {decision.settlement?.has_corrections === true && <small><strong>Finanzkorrektur vorhanden.</strong> Die korrigierte Aufteilung von Provision, Servicegebühr, Partneranteil und Barzahlung ist noch nicht bestätigt.</small>}</td>
                <td>{OUTCOMES[decision.outcome] || UNKNOWN}<small>{decision.issued_at ? 'Serverausgabe bestätigt' : 'Serverausgabe nicht bestätigt'}</small>
                  <small>{reasonLabel(decision.learning_reason)}</small></td>
              </tr>)}</tbody>
            </table>
          </div>
          {snapshot.decisions.length === 0 && <p>Noch keine Entscheidungen vorhanden.</p>}
        </Card>
      </>}
    </section>
  );
}
